import { useState, type ChangeEvent } from "react";
import { css } from "@emotion/react";
import CheckIcon from "@mui/icons-material/Check";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { Element } from "react-scroll";

import type { IconType } from "../../types/subText.js";
import { FormLabel } from "../FormLabel.js";
import { HelperText } from "../HelperText/HelperText.js";
import { ReadOnlyContent } from "../ReadOnlyContent/ReadOnlyContent.js";

type OptionsType =
  | Array<{
      label: string;
      subtext?: string;
      value: string;
    }>
  | any[];

export interface RadioButtonProps {
  name: string;
  label?: string;
  options: OptionsType;
  subtext?: string;
  subtextIcon?: IconType;
  errorMessage?: string;
  selected?: string | boolean;
  vertical?: boolean;
  hideIcons?: boolean;
  onChange?: (e: any) => void;
  solid?: boolean;
  validateInput?: boolean;
  disabled?: boolean;
  isApplyErrorStyle?: boolean;
  useBottomPadding?: boolean;
  isYesNoButton?: string | boolean;
  onClick?: () => void;
  readOnly?: boolean;
  isHighlighted?: boolean;
}

const defaultProps: Partial<RadioButtonProps> = {
  label: undefined,
  subtext: undefined,
  errorMessage: undefined,
  selected: undefined,
  vertical: undefined,
  solid: false,
  hideIcons: undefined,
  onChange: () => {},
  validateInput: undefined,
  disabled: false,
  isApplyErrorStyle: undefined,
  subtextIcon: undefined,
  useBottomPadding: true,
  isYesNoButton: false,
  readOnly: false,
  isHighlighted: false,
};

export const RadioButton = (props: RadioButtonProps) => {
  const allProps: RadioButtonProps = { ...defaultProps, ...props };
  const {
    name,
    label,
    options,
    subtext,
    subtextIcon,
    errorMessage,
    selected,
    vertical,
    hideIcons,
    onChange,
    solid,
    validateInput,
    disabled,
    isApplyErrorStyle,
    useBottomPadding,
    isYesNoButton,
    readOnly,
    isHighlighted,
  } = allProps;
  const [error, setError] = useState(errorMessage);
  const isSelected = (item: any) => item?.value === selected;

  const theme = useTheme();

  const hasError = errorMessage || error;
  const applySelectedRadioButtonStyle = () => {
    if (!solid) return "";
    if ((hasError || isApplyErrorStyle) && !disabled) {
      return theme.palette.error.main;
    }
    if (disabled) return theme.palette.text.disabled;
    return theme.palette.primary.main;
  };

  const styles = {
    radioButton: css`
      background: ${solid ? theme.palette.background.default : ""};
      padding: ${solid ? theme.spacing(1, 2, 1, 0) : theme.spacing(1, 1, 1, 0)};
      margin: ${theme.spacing(1)}px 0;
      margin-bottom: ${vertical ? theme.spacing(1) : 0};
      margin-right: ${vertical ? 0 : theme.spacing(2)};
      font-size: ${theme.spacing(2.25)};
    `,
    selectedRadioButton: css`
      background: ${applySelectedRadioButtonStyle()};
      & .MuiFormControlLabel-label.Mui-disabled {
        color: ${solid ? theme.palette.common.white : ""};
      }
    `,
    checkboxColor: css`
      color: ${disabled ? theme.palette.text.hint : theme.palette.text.primary};
    `,
    optionLabel: css`
      font-weight: 500;
      font-size: ${theme.spacing(2.25)};
    `,
    solidGroup: css`
      color: ${disabled ? theme.palette.text.hint : theme.palette.text.primary};
    `,
    buttonPaddingController: css`
      width: 100%;
      padding-bottom: ${useBottomPadding ? theme.spacing(4) : ""};
    `,
    formControlClass: css`
      width: 100%;
    `,
    subtext: css`
      font-weight: 400;
      font-size: ${theme.spacing(2)};
    `,
  };

  const defaultRadioStyle = solid && styles.solidGroup;

  const createMarkup = (optionLabel: string) => {
    return { __html: optionLabel };
  };

  const validate = (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const checked = e.target.value;
    if (checked === "") {
      setError(errorMessage);
    }
  };

  const handleOnChange = (e: any) => {
    if (disabled) return;

    validateInput && validate(e);
    const value = isYesNoButton ? e.target.value === "true" : e.target.value;
    onChange && onChange(value);
  };

  const getRadioIcon = (item: any) => (
    <Radio
      checked={isSelected(item)}
      style={{ ...(hideIcons && { display: "none" }) }}
      checkedIcon={<CheckIcon css={styles.checkboxColor} />}
    />
  );

  const getKey = (index: number) => `${name}-${index}`;

  if (readOnly) {
    const selectedItem = options.find((item) => isSelected(item)) || [];
    return (
      <ReadOnlyContent
        label={label}
        content={[selectedItem?.label]}
        isHighlighted={isHighlighted}
        subtextIcon={subtextIcon!}
        dense
      />
    );
  }

  return (
    <Element name={name}>
      <FormControl
        component="fieldset"
        css={
          useBottomPadding
            ? styles.buttonPaddingController
            : styles.formControlClass
        }
      >
        {label && <FormLabel label={label} error={!!(error || errorMessage)} />}
        <RadioGroup
          className={`${defaultRadioStyle}`}
          row={!vertical}
          onChange={handleOnChange}
        >
          {options.map((item, index) => (
            <FormControlLabel
              css={[
                styles.radioButton,
                isSelected(item) ? styles.selectedRadioButton : "",
              ]}
              key={getKey(index)}
              value={item.value}
              control={getRadioIcon(item)}
              label={
                <>
                  {item.label && (
                    <Typography css={styles.optionLabel}>
                      <span
                        dangerouslySetInnerHTML={createMarkup(item.label)}
                      />
                    </Typography>
                  )}
                  {item.subtext && (
                    <Typography css={styles.subtext}>{item.subtext}</Typography>
                  )}
                </>
              }
              labelPlacement="end"
              disabled={disabled}
            />
          ))}
        </RadioGroup>
        {(subtext || errorMessage || error) && (
          <HelperText subtext={subtext} errorMessage={errorMessage || error} />
        )}
      </FormControl>
    </Element>
  );
};
